import { gql } from "@apollo/client"
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist"
import { uuid } from "config/app-constants"
import { graph } from "config/axios-context"
import { BookingChannelDatalistQuery, BookingChannelDatalistQueryVariables, BookingChannelFilterInput, BookingChannelSortInput, CommissionTypes, SortEnumType } from "types/graphql"
import { appendFilters } from "utilities/datalist/datalist-filter-utilities"
import useAppContext from "utilities/hooks/useAppContext"
import RoomTypeView from "../roomType/RoomTypeView"
import { Roles } from "utilities/app-utilities"
import { AppRoutes } from "config/router"
import { faBedPulse, faDollarSign, faFileInvoiceDollar, faHashtag } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { deleteMutation } from "utilities/entities/common/delete-mutation"

type BookingChannelDatalistProps = {
    name: string,
    fixedFilters?: BookingChannelFilterInput,
}

type DataItem = NonNullable<BookingChannelDatalistQuery['bookingChannels']>['items'][0];

const query = gql`
query BookingChannelDatalist($where: BookingChannelFilterInput, $order: [BookingChannelSortInput!], $skip: Int, $take: Int) {
    bookingChannels {
      count(where: $where)
      items(where: $where, skip: $skip, take: $take, order: $order) {
        commission
        type
        name
        id
        roomTypeDefultPrices {
          items {
            roomType {
              id
              color
              name
            }
            defaultPrice
            id
          }
        }
      }
    }
  }
`;

const BookingChannelDatalist = (props: BookingChannelDatalistProps) => {
    const context = useAppContext();

    const datalistConfig: DatalistPropsType<DataItem, BookingChannelFilterInput, BookingChannelSortInput> = {
        name: props.name + 'booking-channel-datalist',
        dataSource: {
            request: async params => {
                var variables: BookingChannelDatalistQueryVariables = {
                    where: params.filters,
                    order: params.sort,
                    skip: params.skip,
                    take: params.take
                }

                if (params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        name: {
                            contains: params.searchText
                        }
                    })
                }

                if (props.fixedFilters) {
                    variables.where = appendFilters(variables.where, props.fixedFilters);
                }

                try {
                    const response = await graph<BookingChannelDatalistQuery, BookingChannelDatalistQueryVariables>({
                        query,
                        variables
                    });

                    if (response.errors?.length) {
                        throw response.errors;
                    }

                    return response.data!.bookingChannels!;
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    name: 'name',
                    id: uuid(),
                    direction: SortEnumType.Asc
                }
            ]
        },
        itemId(i) {
            return i.id;
        },
        table: {
            columns: [
                {
                    name: 'id',
                    card: {
                        hide: true
                    }
                },
                {
                    name: 'name',
                    sort: true,
                    filter: {
                        type: 'string'
                    },
                    card: {
                        icon: faHashtag,
                        group: 'general'
                    }
                },
                {
                    name: 'commission',
                    resolve: i => i.commission + (i.type === CommissionTypes.Percent ? ' %' : ' €'),
                    sort: true,
                    filter: {
                        type: 'number',
                    },
                    card: {
                        icon: faFileInvoiceDollar,
                        group: 'general'
                    }
                },
                {
                    name: 'defaultPrice',
                    resolve: i => <div>
                        {i.roomTypeDefultPrices.items.map(r => {
                            return <span key={r.id} className="border-l theme-border first:border-transparent">
                                <RoomTypeView name={r.defaultPrice} color={r.roomType.color} />
                            </span>
                        })}
                    </div>,
                    card: {
                        group: 'general',
                        resolve: i => <div>
                            <FontAwesomeIcon icon={faDollarSign} />
                            {i.roomTypeDefultPrices.items.map((r, i) => {
                                return <span key={r.id} className={`border-l ${i > 0 ? 'theme-border' : 'border-transparent'}`}>
                                    <RoomTypeView name={r.defaultPrice} color={r.roomType.color} />
                                </span>
                            })}
                        </div>
                    }
                }
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.ADMIN,
                    delete: async (item: DataItem, e: React.MouseEvent) => {
                        await deleteMutation('bookingChannelDelete', item.id);
                    },
                    editUrl: AppRoutes.ACCOMMODATION_CHANNELS_EDIT
                }
            }
        }
    }

    return <Datalist {...datalistConfig} />
}

export default BookingChannelDatalist;
